import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import styled from "styled-components";
import { checkTaskApi, getTasksApi } from "../../api/task";
import { useSelector, useDispatch } from "react-redux";
import { RootState, TaskType } from "../../redux/type";
import { useNavigate } from "react-router-dom";
import { setTasks, setUserData } from "../../redux/actions";
import { formatNumber } from "../../utils/helpers";
import { ArrowRight, Clock } from "lucide-react";
import { formatCountdown } from "./TaskDetailsModal";

// Styled Components
const Button = styled.button<{ $mini?: boolean }>`
  background: #1a1a1a;
  backdrop-filter: blur(5px);
  color: white;
  padding: ${(props) => (props.$mini ? "0.75rem" : "1rem")};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: ${(props) => (props.$mini ? "0.8rem" : "0.9rem")};
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-weight: 600;
  gap: 1rem;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
`;

const MiniButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: space-between;
`;

const RewardBadge = styled.span<{ type?: "primary" | "secondary" }>`
  background: ${(props) =>
    props.type === "primary" ? "#115520ff" : "#3b82f6"};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ButtonImage = styled.div<{ $mini?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${(props) => (props.$mini ? "8px" : "12px")};
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  font-size: ${(props) =>
    props.$mini ? "1.25rem" : "1.5rem"}; /* Emoji size */
`;

const ButtonClick = styled.button<{ $status: string; $mini?: boolean }>`
  background: none;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  font-size: ${(props) => (props.$mini ? "0.7rem" : "0.8rem")};
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: ${(props) => (props.$mini ? "0.5rem 0.75rem" : "0.7rem 1rem")};
  text-transform: capitalize;
  min-width: ${(props) => (props.$mini ? "70px" : "90px")};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  width: 100%;

  &:hover {
    ${(props) =>
      props.$status !== "completed" &&
      `
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    `}
  }

  &:disabled {
    cursor: not-allowed;
    background: linear-gradient(135deg, #7a7a7a, #999999);
    transform: none !important;
    box-shadow: none !important;
  }

  ${(props) =>
    props.$status !== "completed" &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      transition: none;
    }
  `}
`;

const Tasks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BigTitle = styled.div`
  font-size: 1.2rem;
  margin: 0 0.5rem;
  font-weight: 800;
  color: white;
  background: linear-gradient(90deg, #ccff00, #00b4db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0.5rem;
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, #ccff00, #00b4db);
    border-radius: 3px;
  }
`;

const ButtonTitle = styled.div<{ $mini?: boolean }>`
  font-size: ${(props) => (props.$mini ? "0.7rem" : "0.9rem")};
  font-weight: 700;
  text-align: left;
  color: white;
  text-transform: capitalize;
`;

const MiniButtonSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const InfoColumn = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ActionRow = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

const Subtitle = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.65rem;
  margin-top: 0.5rem;
  text-align: left;
`;

const ButtonTasksContainer = styled.div`
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const TasksGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TimerBadge = styled.div<{ $late?: boolean }>`
  background: ${({ $late }) =>
    $late ? "rgba(255, 100, 0, 0.15)" : "#1a1a1a20"};
  color: ${({ $late }) => ($late ? "#FFA500" : "#238833")};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid ${({ $late }) => ($late ? "#FFA500" : "#238833")};
`;

interface ButtonWithIconProps {
  name: string;
  endsIn?: string; // Add endsIn prop
  subtitle: string;
  checkTask: () => void;
  image?: string;
  user: any;
  mini?: boolean;
}

export const getTaskEmoji = (type: TaskType): string => {
  const emojiMap: Record<TaskType, string> = {
    [TaskType.DECODER]: "🧠",
    [TaskType.LOREKEEPER]: "📚",
    [TaskType.SIGNAL_BOOSTER]: "📡",
    [TaskType.FEED_WATCHER]: "🕵️‍♀️",
    [TaskType.NETWORKING_EVENT]: "🤝",
    [TaskType.UI_UX_ARCHITECT]: "🎨",
    [TaskType.INTERFACE_ARCHITECT]: "🧩",
    [TaskType.SYSTEM_BUILDER]: "🛠",
  };

  return emojiMap[type] || "❓";
};

const ButtonWithIcon: React.FC<ButtonWithIconProps> = ({
  name,
  subtitle,
  checkTask,
  image,
  user,
  mini = false,
  endsIn,
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState<string>("");
  const [isLate, setIsLate] = useState<boolean>(false);

  useEffect(() => {
    if (!endsIn) return;

    const endTime = new Date(endsIn).getTime();

    const interval = setInterval(() => {
      const now = Date.now();
      const remainingMs = endTime - now;

      if (remainingMs <= 0) {
        setCountdown("Late");
        setIsLate(true);
        clearInterval(interval);
        return;
      }

      const remaining = formatCountdown(new Date(endTime));
      setCountdown("Ends in " + remaining);
      setIsLate(false);
    }, 1000);

    // Initial call
    const initialRemaining = endTime - Date.now();
    if (initialRemaining <= 0) {
      setCountdown("Late");
      setIsLate(true);
    } else {
      setCountdown("Ends in " + formatCountdown(new Date(endTime)));
      setIsLate(false);
    }

    return () => clearInterval(interval);
  }, [endsIn]);

  if (mini) {
    return (
      <Button $mini={mini}>
        <MiniButtonContent
          onClick={() => {
            navigate(`/task`);
          }}
        >
          <MiniButtonSection>
            <ButtonImage $mini={mini}>
              {getTaskEmoji(name as TaskType)}
            </ButtonImage>
            <ButtonTitle $mini={mini}>{subtitle}</ButtonTitle>
          </MiniButtonSection>
          <MiniButtonSection>
            {endsIn && (
              <TimerBadge $late={isLate}>
                <Clock size={12} color={isLate ? "#FFA500" : "#238833"} />
                {countdown}
              </TimerBadge>
            )}
            <ArrowRight size={16} />
          </MiniButtonSection>
        </MiniButtonContent>
      </Button>
    );
  }

  return (
    <Button $mini={mini}>
      <TopRow onClick={checkTask}>
        <ButtonImage $mini={mini}>{getTaskEmoji(name as TaskType)}</ButtonImage>
        <InfoColumn>
          <ButtonTitle $mini={mini}>{name}</ButtonTitle>
          <Subtitle>{subtitle}</Subtitle>
          {endsIn && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                marginTop: "0.25rem",
              }}
            >
              <TimerBadge $late={isLate}>
                <Clock size={12} color={isLate ? "#FFA500" : "#238833"} />
                {countdown}
              </TimerBadge>
            </div>
          )}
        </InfoColumn>
        <ArrowRight size={18} />
      </TopRow>
    </Button>
  );
};

interface ButtonTasksProps {
  mini?: boolean;
  limit?: number;
  onTaskClick?: (task: any) => void;
  roleFilter?: string; // 🔹 New prop
}

const ButtonTasks: React.FC<ButtonTasksProps> = ({
  mini = false,
  limit = 0,
  onTaskClick,
  roleFilter = "all",
}) => {
  const tasks = useSelector((state: RootState) => state.tasks);
  const user = useSelector((state: RootState) => state.userData);

  if (!tasks) return null;

  let filteredTasks = tasks.filter((task) => {
    const matchesRole = roleFilter === "all" ? true : task.type === roleFilter;
    return matchesRole;
  });

  // ✅ Sort active first, late last
  filteredTasks = filteredTasks.sort((a, b) => {
    const now = Date.now();
    const aTime = new Date(a.endDate).getTime();
    const bTime = new Date(b.endDate).getTime();

    const aLate = aTime < now;
    const bLate = bTime < now;

    if (aLate && !bLate) return 1; // push a down
    if (!aLate && bLate) return -1; // keep b down
    return aTime - bTime; // otherwise sort by soonest deadline
  });

  if (limit) filteredTasks = filteredTasks.slice(0, limit);

  const renderTasks = (taskList) =>
    taskList.map((task) => (
      <ButtonWithIcon
        key={task._id}
        image={""}
        name={task.type}
        subtitle={task.title}
        checkTask={() => onTaskClick && onTaskClick(task)}
        user={user}
        mini={mini}
        endsIn={task.endDate}
      />
    ));

  return (
    <ButtonTasksContainer>
      {filteredTasks.length > 0 ? (
        <TasksGroup>{renderTasks(filteredTasks)}</TasksGroup>
      ) : (
        <div style={{ color: "white", opacity: 0.6, textAlign: "center" }}>
          No tasks found.
        </div>
      )}
    </ButtonTasksContainer>
  );
};

export default ButtonTasks;
